import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, map, filter } from 'rxjs/operators';

import { SchoolInfo, ApplicationService, SchoolGroupInfo, EligibilitySaveParameters } from '../../services/application/application.service';
import { AwardsService, AwardProgramInfo } from '../../services/awards/awards.service';
import { ErrorService } from '../../services/error/error.service';
import { SettingsService } from '../../services/settings/settings.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserService, UserInfo } from '../../services/user/user.service';

@Component({
	selector: 'app-schooleligibility',
	templateUrl: './schooleligibility.component.html',
	styleUrls: ['./schooleligibility.component.scss']
})

export class SchooleligibilityComponent implements OnInit, OnDestroy {
	private _subrelease: Subject<void> = new Subject<void>();

    AllSchools: SchoolInfo[];
	AvailableSchoolGroups: SchoolGroupInfo[];
	unsortedSchools: SchoolInfo[] = [];
    AvailableSchools: SchoolInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchoolYear: string;
    eligibilityForm: FormGroup;
    eligibilityArray: FormArray;
    User: UserInfo;
	ViewReady: boolean;
	loading: boolean = true;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private errorSvc: ErrorService,
        private fb: FormBuilder,
        private splashSvc: SplashService,
		private userSvc: UserService,
		private settingsSvc: SettingsService
    ) { }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AwardInfo = null;
        self.CurrentSchoolYear = null;
        self.ViewReady = false;
        self.User = self.userSvc.User;

        combineLatest(
            self.awardSvc.CurrentAwardsSub,
			self.settingsSvc.CurrentSchoolYearSubject
        ).pipe(
            takeUntil(self._subrelease),
            map(_pck => {
                return {
                    Awards: _pck[0],
                    Year: _pck[1],
                };
            }),
            filter(pck => pck && !!pck.Awards)
        ).subscribe(packet => {
            // Get the current award program
            self.AwardInfo = packet.Awards;
            self.CurrentSchoolYear = packet.Year;

            // Load everything we need for this page
			const allSchoolsObs = self.appSvc.GetAllSchools(self.CurrentSchoolYear);
			const allSchoolGrpsObs = self.appSvc.GetAllSchoolGroups(self.AwardInfo.AwardID, self.CurrentSchoolYear);
			const schoolEligibilityObs = self.appSvc.GetSchoolEligibility(self.AwardInfo.AwardID, self.CurrentSchoolYear);

            combineLatest(allSchoolsObs, allSchoolGrpsObs, schoolEligibilityObs).pipe(
                takeUntil(self._subrelease),
                map(_spck => {
                    return {
                        Schools: _spck[0],
                        Groups: _spck[1],
                        EligibilityList: _spck[2]
                    };
                }),
                filter(p => !!p && !!p.Schools && !!p.Groups)
            ).subscribe(spacket => {
                self.AllSchools = spacket.Schools || [];
                self.AvailableSchoolGroups = spacket.Groups || [];
                let eligibleSchools = spacket.EligibilityList;

                // Parse the schools by iterating all schools. In instances where the school number is part of a
                // known school group, skip adding that school. Once we're done, add in the school groups, and then
                // sort by the name of the school/group lead.
                self.AllSchools.forEach(sch => {
                    const grp = self.AvailableSchoolGroups.find(
                        item => {
                            if (item.LeadLocationNumber === sch.SchoolNumber) {
                                return true;
                            }
                            if (item.Schools) {
                                const sgrp = item.Schools.find(sitem => sitem.LocationNumber === sch.SchoolNumber);
                                if (sgrp) {
                                    return true;
                                }
                            }
                            return false;
                        }
                    );

					if (!grp) {
						this.unsortedSchools.push(sch);
                    }
                });

                // Now we can also add in the school groups by their leader
                self.AvailableSchoolGroups.forEach(sch => {
					let lead = self.AllSchools.find(s => s.SchoolNumber === sch.LeadLocationNumber);

					let nums: string[] = [];
					for (let s of sch.Schools) {
						nums.push(s.LocationNumber);
					}

					this.unsortedSchools.push(<SchoolInfo>{
                        SchoolName: sch.Name,
                        SchoolNumber: sch.LeadLocationNumber,
                        IsElementary: ((lead != null) ? lead.IsElementary : false),
                        IsMiddle: ((lead != null) ? lead.IsMiddle : false),
						IsSenior: ((lead != null) ? lead.IsSenior : false),
						SchoolNumberList: nums.join(', '),
						InSchoolGroup: true
                    });
                });

				//Add eligibility
				for (let s of this.unsortedSchools) {
                    s.AwardEligibility = [];
                    for (let e of eligibleSchools) {
                        if (s.SchoolNumber === e.Display) {
                            s.AwardEligibility.push(e.Value);
						}
					}
				}

				this.loading = false;

            }, error => self.errorSvc.HandleServiceError(error));
        });

	}

	sortSchoolsByName(sortByName: boolean) {
		// Lastly, sort and store
		if (sortByName) {
			this.AvailableSchools = this.unsortedSchools.sort((a, b) =>
				a.SchoolName.localeCompare(b.SchoolName)
			);
		}
		else {
			this.AvailableSchools = this.unsortedSchools.sort((a, b) =>
				a.SchoolNumber.localeCompare(b.SchoolNumber)
			);
		}

		this.createEligibilityForm();
	}

    createEligibilityForm() {
        this.eligibilityForm = this.fb.group({
            EligibilityList: this.fb.array([])
        });

        this.createEligibilityArray();
    }

    createEligibilityArray() {
        this.eligibilityArray = this.eligibilityForm.get('EligibilityList') as FormArray;

        for (let s of this.AvailableSchools) {
            this.eligibilityArray.push(this.createEligibilityType(s));
        }

        //eligibilityArray.valueChanges.pipe(takeUntil(this._subrelease)).subscribe(values => {});

        this.ViewReady = true;
    }

    createEligibilityType(element: SchoolInfo): FormGroup {
        return this.fb.group({
            SchoolName: element.SchoolName,
            SchoolNumber: element.SchoolNumber,
			IsChecked: element.AwardEligibility.includes(this.AwardInfo.AwardID),
			InSchoolGroup: element.InSchoolGroup,
			SchoolNumberList: element.SchoolNumberList
        });
    }

    SelectAll() {
        for (let s of this.eligibilityArray.controls) {
            s.patchValue({IsChecked: true});
		}
	}

    submit(form: any) {
        let parameters: EligibilitySaveParameters = new EligibilitySaveParameters();
        parameters.AuthToken = this.User.AuthToken;
        parameters.CurrentRole = this.User.CurrentRole;
        parameters.AwardID = this.AwardInfo.AwardID;
		parameters.Data = this.eligibilityForm.value.EligibilityList;
		parameters.SchoolYear = this.CurrentSchoolYear;

        this.appSvc.SetEligibility(parameters).pipe(takeUntil(this._subrelease)).subscribe((res: any) => {
                this.splashSvc.CloseSplash();
                this.splashSvc.SnackbarSuccess('Eligibility Set Successfully', 5000);
            }, error =>
                this.errorSvc.HandleServiceError(error)
            );
	}

}
